import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import { Toast } from "../Toast";
import { useToast } from "../../hooks/useToast";
import { colors } from "../../theme/designSystem";
import {
  serviceTypeService,
  ServiceTypeItem,
  AttendanceKind,
  ATTENDANCE_KIND_LABELS,
  PACKAGE_CHARGE_MODE_LABELS,
  type PackageChargeMode,
  ServiceOrigin,
  SERVICE_ORIGIN_LABELS,
  PROCEDURE_TABLE_OPTIONS,
  ServiceTypeHealthPlanOption,
  ProcedureCodeCheck,
} from "../../services/serviceTypeService";

interface ProfOption {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  professionalTypes: ProfOption[];
  serviceType: ServiceTypeItem | null; // null = criar
  onSaved: () => void;
}

interface FormState {
  professionalType: string;
  description: string;
  packageAmount: string;
  freeHour: boolean;
  visibleOnSchedule: boolean;
  attendanceKind: AttendanceKind;
  chargePerSession: boolean;
  packageChargeMode: PackageChargeMode;
  active: boolean;
  origin: ServiceOrigin;
  healthPlanId: string;
  tableCode: string;
  procedureCode: string;
}

const emptyForm: FormState = {
  professionalType: "",
  description: "",
  packageAmount: "1",
  freeHour: false,
  visibleOnSchedule: true,
  attendanceKind: "continuous",
  chargePerSession: true,
  packageChargeMode: "total",
  active: true,
  origin: "own",
  healthPlanId: "",
  tableCode: "22",
  procedureCode: "",
};

const ServiceTypeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  entityId,
  professionalTypes,
  serviceType,
  onSaved,
}) => {
  const { toast, showToast, hideToast } = useToast();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState<ServiceTypeHealthPlanOption[]>([]);
  const [codeCheck, setCodeCheck] = useState<ProcedureCodeCheck | null>(null);
  // Cascata (RN-411g): escolhe-se o convênio e depois o plano dele. Só o plano é gravado —
  // a operadora sai do próprio plano ao reabrir.
  const [operatorId, setOperatorId] = useState("");
  const [checkingCode, setCheckingCode] = useState(false);
  const isEdit = !!serviceType;
  const isAgreement = form.origin === "operator";

  useEffect(() => {
    if (!isOpen) return;
    setSaving(false); // garante modal "limpo" a cada abertura (o componente fica montado)
    setCodeCheck(null);
    // O componente não desmonta: sem isto o próximo cadastro abriria com o convênio anterior
    setOperatorId("");
    if (serviceType) {
      setForm({
        professionalType: serviceType.professionalType || "",
        description: serviceType.description || "",
        packageAmount:
          serviceType.packageAmount != null
            ? String(serviceType.packageAmount)
            : "",
        freeHour: !!serviceType.freeHour,
        visibleOnSchedule: !!serviceType.visibleOnSchedule,
        attendanceKind: serviceType.attendanceKind ?? "continuous",
        chargePerSession: serviceType.chargePerSession ?? true,
        packageChargeMode: serviceType.packageChargeMode ?? "total",
        active: serviceType.isActive,
        origin: serviceType.origin ?? "own",
        healthPlanId: serviceType.healthPlanId || "",
        tableCode: serviceType.tableCode || "22",
        procedureCode: serviceType.procedureCode || "",
      });
    } else {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serviceType]);

  // Os planos só são necessários no procedimento de convênio; carrega na primeira vez que o
  // usuário escolhe essa origem, não em toda abertura do modal.
  useEffect(() => {
    if (!isOpen || !isAgreement || plans.length > 0) return;
    serviceTypeService
      .getHealthPlanOptions()
      .then(setPlans)
      .catch((error) => console.error("Erro ao carregar planos:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAgreement]);

  // Plano inativado depois do cadastro continua aparecendo: o serviço existente não pode perder
  // o vínculo só porque o combo lista apenas os que valem hoje.
  const allPlans = React.useMemo(() => {
    const linked = serviceType?.healthPlanId;
    if (!linked || plans.some((p) => p.id === linked)) return plans;
    return [
      ...plans,
      {
        id: linked,
        name: `${serviceType?.healthPlanName || "Plano"} (inativo)`,
        operatorId: "",
        operatorName: "(convênio inativo)",
      },
    ];
  }, [plans, serviceType]);

  const operatorOptions = React.useMemo(() => {
    const byId = new Map<string, string>();
    allPlans.forEach((p) => byId.set(p.operatorId, p.operatorName));
    return Array.from(byId, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [allPlans]);

  const planOptions = React.useMemo(
    () => allPlans.filter((p) => p.operatorId === operatorId),
    [allPlans, operatorId],
  );

  // A operadora do plano já gravado, ao abrir para editar; e o plano único seleciona-se sozinho.
  useEffect(() => {
    if (!isAgreement) return;
    if (form.healthPlanId && !operatorId) {
      const linked = allPlans.find((p) => p.id === form.healthPlanId);
      if (linked) setOperatorId(linked.operatorId);
      return;
    }
    if (operatorId && !form.healthPlanId && planOptions.length === 1) {
      setForm((f) => ({ ...f, healthPlanId: planOptions[0].id }));
    }
  }, [isAgreement, form.healthPlanId, operatorId, allPlans, planOptions]);

  const descriptionMissing = form.description.trim().length === 0;
  const professionalTypeMissing = form.professionalType.length === 0;
  const planMissing = isAgreement && form.healthPlanId.length === 0;
  const procedureCodeMissing = isAgreement && form.procedureCode.length === 0;

  const handleCheckCode = async () => {
    if (checkingCode || form.procedureCode.length === 0) return;
    setCheckingCode(true);
    try {
      setCodeCheck(await serviceTypeService.checkProcedureCode(form.procedureCode));
    } catch (error) {
      console.error("Erro ao conferir o código:", error);
      showToast("Não foi possível conferir o código agora.", "error");
    } finally {
      setCheckingCode(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    if (professionalTypeMissing) {
      showToast("Selecione o tipo de profissional.", "error");
      return;
    }
    if (descriptionMissing) {
      showToast("Informe a descrição do serviço.", "error");
      return;
    }
    if (planMissing) {
      showToast("Selecione o plano que autorizou o procedimento.", "error");
      return;
    }
    if (procedureCodeMissing) {
      showToast("Informe o código do procedimento.", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        description: form.description.trim(),
        professionalType: form.professionalType,
        entity: entityId,
        packageAmount: form.packageAmount
          ? Number(form.packageAmount)
          : undefined,
        freeHour: form.freeHour,
        visibleOnSchedule: form.visibleOnSchedule,
        attendanceKind: form.attendanceKind,
        chargePerSession: form.chargePerSession,
        packageChargeMode: form.packageChargeMode,
        origin: form.origin,
        // Na volta para Próprio os três vão vazios; o backend limpa o que sobrou do convênio.
        healthPlanId: isAgreement ? form.healthPlanId : null,
        tableCode: isAgreement ? form.tableCode : null,
        procedureCode: isAgreement ? form.procedureCode : null,
      };

      if (isEdit && serviceType) {
        await serviceTypeService.update(serviceType.id, {
          ...payload,
          active: form.active,
        });
      } else {
        await serviceTypeService.create(payload);
      }

      onSaved();
      onClose();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao salvar o serviço. Tente novamente.";
      showToast(message, "error");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", position: "relative", overflow: "hidden" },
      }}
    >
      <SavingOverlay open={saving} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
        }}
      >
        {isEdit ? "Editar Serviço" : "Novo Serviço"}
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{ color: "white" }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        <FormControl component="fieldset">
          <FormLabel
            component="legend"
            sx={{ fontSize: "0.8rem", color: colors.textSecondary, "&.Mui-focused": { color: colors.textSecondary } }}
          >
            Origem do serviço
          </FormLabel>
          <RadioGroup
            row
            value={form.origin}
            onChange={(e) => {
              const origin = e.target.value as ServiceOrigin;
              setCodeCheck(null);
              setForm((f) => ({ ...f, origin }));
            }}
          >
            {(Object.keys(SERVICE_ORIGIN_LABELS) as ServiceOrigin[]).map((o) => (
              <FormControlLabel
                key={o}
                value={o}
                control={
                  <Radio sx={{ color: colors.primary, "&.Mui-checked": { color: colors.primary } }} />
                }
                label={SERVICE_ORIGIN_LABELS[o]}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.95rem", color: colors.text } }}
              />
            ))}
          </RadioGroup>
          <Box sx={{ fontSize: "0.78rem", color: colors.textSecondary }}>
            {isAgreement
              ? "Procedimento que a operadora autorizou no credenciamento: carrega o plano e o código do contrato."
              : "Serviço da clínica: nome, preço e repasse decididos por ela."}
          </Box>
        </FormControl>

        {isAgreement && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: 2,
              borderRadius: "8px",
              border: `1px solid ${colors.backgroundAlt}`,
              backgroundColor: colors.background,
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
              <TextField
                select
                label="Convênio"
                value={operatorId}
                onChange={(e) => {
                  setOperatorId(e.target.value);
                  setForm((f) => ({ ...f, healthPlanId: "" }));
                }}
                required
                error={planMissing}
                sx={{ flex: "1 1 240px" }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {operatorOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Plano"
                value={form.healthPlanId}
                onChange={(e) => setForm((f) => ({ ...f, healthPlanId: e.target.value }))}
                required
                error={planMissing}
                disabled={!operatorId}
                helperText={
                  operatorId
                    ? "O valor muda por plano: AMIL e AMIL 400 A 900 são linhas diferentes."
                    : "Escolha o convênio primeiro."
                }
                sx={{ flex: "1 1 240px" }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {planOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name.startsWith(`${p.operatorName} - `)
                      ? p.name.slice(p.operatorName.length + 3)
                      : p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
              <TextField
                select
                label="Tabela"
                value={form.tableCode}
                onChange={(e) => {
                  setCodeCheck(null);
                  setForm((f) => ({ ...f, tableCode: e.target.value }));
                }}
                sx={{ flex: "1 1 260px" }}
                InputLabelProps={{ shrink: true }}
              >
                {PROCEDURE_TABLE_OPTIONS.map((t) => (
                  <MenuItem key={t.code} value={t.code}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Código do procedimento"
                value={form.procedureCode}
                onChange={(e) => {
                  setCodeCheck(null);
                  setForm((f) => ({ ...f, procedureCode: e.target.value.replace(/\D/g, "") }));
                }}
                onBlur={() => {
                  if (form.tableCode === "22") handleCheckCode();
                }}
                required
                error={procedureCodeMissing}
                inputProps={{ inputMode: "numeric", maxLength: 20 }}
                sx={{ flex: "1 1 180px" }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* A conferência é informativa: a operadora manda o código, e ela é quem paga. */}
            {form.tableCode === "22" && (
              <Box sx={{ fontSize: "0.78rem" }}>
                {checkingCode && <span style={{ color: colors.textSecondary }}>Conferindo na tabela da ANS…</span>}
                {!checkingCode && codeCheck?.found && codeCheck.inForce && (
                  <span style={{ color: "#0f5132" }}>ANS: {codeCheck.displayName}</span>
                )}
                {!checkingCode && codeCheck?.found && !codeCheck.inForce && (
                  <span style={{ color: "#664d03" }}>
                    ANS: {codeCheck.displayName} — código já encerrado pela ANS. Confirme com a operadora.
                  </span>
                )}
                {!checkingCode && codeCheck && !codeCheck.found && (
                  <span style={{ color: "#842029" }}>
                    Código não encontrado na tabela 22 da ANS. Confira com a operadora; se for tabela dela, escolha 00.
                  </span>
                )}
                {!checkingCode && !codeCheck && (
                  <span style={{ color: colors.textSecondary }}>
                    Ao sair do campo, o código é conferido na tabela de procedimentos da ANS.
                  </span>
                )}
              </Box>
            )}
          </Box>
        )}

        <TextField
          select
          label="Tipo de profissional"
          value={form.professionalType}
          onChange={(e) =>
            setForm((f) => ({ ...f, professionalType: e.target.value }))
          }
          required
          error={professionalTypeMissing}
          fullWidth
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Selecione</MenuItem>
          {professionalTypes.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Descrição do serviço"
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
          required
          error={descriptionMissing}
          fullWidth
          inputProps={{ maxLength: 255 }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Qtd. por pacote"
          type="number"
          value={form.packageAmount}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              packageAmount: e.target.value.replace(/\D/g, ""),
            }))
          }
          inputProps={{ inputMode: "numeric", min: 1, maxLength: 6 }}
          sx={{ width: 180 }}
          InputLabelProps={{ shrink: true }}
        />

        {Number(form.packageAmount) > 1 && (
          <TextField
            select
            label="Cobrança do pacote"
            value={form.packageChargeMode}
            onChange={(e) =>
              setForm((f) => ({ ...f, packageChargeMode: e.target.value as PackageChargeMode }))
            }
            helperText={
              form.packageChargeMode === "total"
                ? "Toda sessão da série aparece no caixa e cobra, mesmo com falta ou cancelamento: quem decide cobrar, dar desconto ou remanejar é o operacional."
                : "Só a sessão com presença que cobra entra no caixa, como em qualquer serviço avulso."
            }
            sx={{ width: 320 }}
            InputLabelProps={{ shrink: true }}
          >
            {(Object.keys(PACKAGE_CHARGE_MODE_LABELS) as PackageChargeMode[]).map((k) => (
              <MenuItem key={k} value={k}>
                {PACKAGE_CHARGE_MODE_LABELS[k]}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          label="Tipo de atendimento"
          value={form.attendanceKind}
          onChange={(e) =>
            setForm((f) => ({ ...f, attendanceKind: e.target.value as AttendanceKind }))
          }
          helperText={
            form.attendanceKind === "punctual"
              ? "Termina em si (avaliação, avulso, reserva): não gera alerta de risco de evasão."
              : "Tratamento que se repete: a ausência prolongada vira alerta de risco de evasão."
          }
          sx={{ width: 320 }}
          InputLabelProps={{ shrink: true }}
        >
          {(Object.keys(ATTENDANCE_KIND_LABELS) as AttendanceKind[]).map((k) => (
            <MenuItem key={k} value={k}>
              {ATTENDANCE_KIND_LABELS[k]}
            </MenuItem>
          ))}
        </TextField>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.chargePerSession}
                onChange={(e) =>
                  setForm((f) => ({ ...f, chargePerSession: e.target.checked }))
                }
                sx={{ color: colors.primary, "&.Mui-checked": { color: colors.primary } }}
              />
            }
            label="Conta como atendimento"
          />
          {form.chargePerSession ? (
            <Box sx={{ fontSize: "0.78rem", color: "#6c757d", marginTop: "-4px" }}>
              Entra no volume de atendimentos e nos indicadores (ticket médio, taxas e metas) e
              na fila de evoluções. Não decide o valor cobrado: isso é a presença e, no pacote, a
              "Cobrança do pacote".
            </Box>
          ) : (
            <Box sx={{ fontSize: "0.78rem", color: "#664d03", marginTop: "-4px" }}>
              Fora do volume de atendimentos e dos indicadores (ticket médio, taxas e metas);
              exibido à parte nas estatísticas. Uso: reserva de agenda de parceiro e aula por
              mensalidade. A ocupação de salas continua contando.
            </Box>
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={form.visibleOnSchedule}
              onChange={(e) =>
                setForm((f) => ({ ...f, visibleOnSchedule: e.target.checked }))
              }
              sx={{
                color: colors.primary,
                "&.Mui-checked": { color: colors.primary },
              }}
            />
          }
          label="Visível na agenda"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={form.freeHour}
              onChange={(e) =>
                setForm((f) => ({ ...f, freeHour: e.target.checked }))
              }
              sx={{
                color: colors.primary,
                "&.Mui-checked": { color: colors.primary },
              }}
            />
          }
          label="Hora livre"
        />
      </DialogContent>

      <DialogActions
        sx={{
          padding: "1.5rem 2rem",
          borderTop: `1px solid ${colors.backgroundAlt}`,
          backgroundColor: colors.background,
          gap: "1rem",
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: colors.primary,
            "&:hover": { backgroundColor: colors.primaryHover },
          }}
        >
          Salvar
        </Button>
      </DialogActions>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Dialog>
  );
};

export default ServiceTypeModal;
